import { createDraggable, eases } from 'animejs';

const ITEM = '[data-anime-sortable-item]';
const HANDLE = '[data-anime-sortable-handle]';

/**
 * @param {HTMLElement} container
 * @param {number} clientY
 * @param {HTMLElement} exclude
 */
function getDragAfterElement(container, clientY, exclude) {
    const children = [...container.querySelectorAll(ITEM)].filter((el) => el !== exclude);

    return children.reduce(
        (closest, child) => {
            const box = child.getBoundingClientRect();
            const offset = clientY - box.top - box.height / 2;
            if (offset < 0 && offset > closest.offset) {
                return { offset, element: child };
            }
            return closest;
        },
        { offset: Number.NEGATIVE_INFINITY, element: null },
    ).element;
}

/**
 * @param {HTMLElement} container
 * @param {HTMLElement} dragged
 * @param {number} clientY
 * @returns {boolean} whether the DOM order changed
 */
function applyRowOrderChange(container, dragged, clientY) {
    const afterElement = getDragAfterElement(container, clientY, dragged);

    if (afterElement != null && dragged.nextElementSibling === afterElement) {
        return false;
    }
    if (afterElement == null) {
        const items = [...container.querySelectorAll(ITEM)];
        if (items.length && items[items.length - 1] === dragged) {
            return false;
        }
        container.appendChild(dragged);
        return true;
    }
    container.insertBefore(dragged, afterElement);
    return true;
}

/**
 * Live reorder while dragging: pointer hit-test + compensate translateY so layout moves don’t
 * stack on the anime offset (refresh only after setY so bounds stay consistent).
 *
 * @param {HTMLElement} container
 * @param {HTMLElement} dragged
 * @param {import('animejs').Draggable} draggable
 */
let lastSwapY = null;
const MIN_MOVE_AFTER_SWAP = 12;

function placeDraggedRowDuringDrag(container, dragged, draggable) {
    const state = container._sortPointerState;
    const rect = dragged.getBoundingClientRect();
    const clientY =
        state != null && Number.isFinite(state.clientY)
            ? state.clientY
            : rect.top + rect.height / 2;

    if (lastSwapY !== null && Math.abs(clientY - lastSwapY) < MIN_MOVE_AFTER_SWAP) {
        return;
    }

    const topBefore = rect.top;

    if (!applyRowOrderChange(container, dragged, clientY)) {
        return;
    }

    lastSwapY = clientY;

    const topAfter = dragged.getBoundingClientRect().top;
    const layoutShift = topAfter - topBefore;

    if (Math.abs(layoutShift) > 0.5) {
        // Patch anime's internal cumulative coords so the next update() frame
        // doesn't re-apply the old (pre-move) position.
        draggable.coords[1] -= layoutShift;
        draggable.coords[3] -= layoutShift;
        // Update visual translateY immediately (setY alone doesn't touch coords).
        draggable.setY(draggable.y - layoutShift, true);
    }
    // Do NOT call refresh() here — it recalculates bounds from the new layout
    // position and clobbers the coordinate compensation.
}

/**
 * @param {HTMLElement} container
 */
function beginSortPointerTracking(container) {
    if (typeof container._sortPointerCleanup === 'function') {
        container._sortPointerCleanup();
    }

    const state = { clientY: 0 };

    const syncY = (e) => {
        if ('clientY' in e && typeof e.clientY === 'number') {
            state.clientY = e.clientY;
        } else if (e.touches && e.touches[0]) {
            state.clientY = e.touches[0].clientY;
        }
    };

    document.addEventListener('pointermove', syncY, { passive: true, capture: true });
    document.addEventListener('touchmove', syncY, { passive: true, capture: true });

    container._sortPointerState = state;
    container._sortPointerCleanup = () => {
        document.removeEventListener('pointermove', syncY, { capture: true });
        document.removeEventListener('touchmove', syncY, { capture: true });
        delete container._sortPointerState;
        delete container._sortPointerCleanup;
    };
}

/**
 * @param {HTMLElement} container
 */
function endSortPointerTracking(container) {
    if (typeof container._sortPointerCleanup === 'function') {
        container._sortPointerCleanup();
    }
}

/**
 * @param {HTMLElement} container
 */
function buildOrderPayload(container) {
    const nodes = container.querySelectorAll(ITEM);
    const r = [];
    nodes.forEach((node, i) => {
        r.push({
            order: i + 1,
            value: node.getAttribute('data-anime-sortable-item'),
        });
    });
    return r;
}

/**
 * @param {{ expression: unknown }} directive
 */
function directiveMethodName(directive) {
    const raw = directive.expression;
    if (raw == null) {
        return null;
    }
    const s = typeof raw === 'string' ? raw.trim() : String(raw).trim();
    return s.replace(/^['"]|['"]$/g, '');
}

let animeSortableDirectiveRegistered = false;

/** @param {{ directive: (name: string, cb: Function) => void }} Livewire */
export function registerLivewireAnimeSortable(Livewire) {
    if (animeSortableDirectiveRegistered) {
        return;
    }
    animeSortableDirectiveRegistered = true;

    Livewire.directive('anime-sortable', ({ el, directive, component }) => {
        if (directive.modifiers.length > 0) {
            return;
        }

        const method = directiveMethodName(directive);
        if (!method) {
            return;
        }

        const container = /** @type {HTMLElement} */ (el);

        if (typeof container._animeSortableCleanup === 'function') {
            container._animeSortableCleanup();
        }

        const instances = [];

        container.querySelectorAll(ITEM).forEach((item) => {
            const row = /** @type {HTMLElement} */ (item);
            const handle = row.querySelector(HANDLE);
            if (!handle) {
                return;
            }

            const d = createDraggable(row, {
                trigger: handle,
                container,
                x: false,
                y: true,
                releaseEase: eases.linear,
                cursor: { onHover: 'grab', onGrab: 'grabbing' },
                onGrab(draggable) {
                    lastSwapY = null;
                    beginSortPointerTracking(container);
                    const r = draggable.$target.getBoundingClientRect();
                    if (container._sortPointerState) {
                        container._sortPointerState.clientY = r.top + r.height / 2;
                    }
                    draggable.$target.classList.add('anime-sortable-item--dragging');
                },
                onDrag(draggable) {
                    placeDraggedRowDuringDrag(container, draggable.$target, draggable);
                },
                onRelease(draggable) {
                    const dragged = draggable.$target;
                    const state = container._sortPointerState;
                    const clientY =
                        state != null && Number.isFinite(state.clientY)
                            ? state.clientY
                            : dragged.getBoundingClientRect().top +
                              dragged.getBoundingClientRect().height / 2;

                    endSortPointerTracking(container);

                    draggable.stop();
                    applyRowOrderChange(container, dragged, clientY);
                    draggable.setY(0, true);
                    draggable.setX(0, true);
                    draggable.refresh();

                    dragged.classList.remove('anime-sortable-item--dragging');

                    setTimeout(() => {
                        component.$wire.call(method, buildOrderPayload(container));
                    }, 1);
                },
            });

            instances.push(d);
        });

        container._animeSortableCleanup = () => {
            endSortPointerTracking(container);
            instances.forEach((inst) => inst.revert());
            delete container._animeSortableCleanup;
        };
    });
}
